import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { tokenizeLine, DEFAULT_WORDS_PER_MINUTE } from "./documentBodyUtils";

// Splits a bit's content into lines of word pieces for the scroll view.
// Each non-space piece becomes its own span; `timed` marks the
// piece that starts a spoken word (stage directions are untimed), which
// mirrors getBitTimings' word count so the scroll and the editor's bit
// timings agree. Runs of blank lines collapse into a single blank line.
function buildLines(content) {
  const lines = [];
  let lastBlank = true; // also drops leading blank lines
  content.forEach((raw) => {
    if (raw.trim() === "") {
      if (!lastBlank) lines.push(null);
      lastBlank = true;
      return;
    }
    lastBlank = false;
    const pieces = [];
    let atWordStart = true;
    tokenizeLine(raw).forEach((token) => {
      token.display.split(/(\s+)/).forEach((text) => {
        if (!text) return;
        if (/^\s+$/.test(text)) {
          pieces.push({ text, space: true });
          atWordStart = true;
          return;
        }
        pieces.push({ text, type: token.type, timed: atWordStart && token.type !== "stage" });
        atWordStart = false;
      });
    });
    lines.push(pieces);
  });
  if (lines.length && lines[lines.length - 1] === null) lines.pop();
  return lines;
}

// Rows of history kept visible above the current row
const HISTORY_ROWS = 2;
// Row text size in px, a little larger on wider screens
const FONT_SM = 20;
const FONT_MD = 34;
// The current row is marked by a bar at its left edge; every row reserves
// the bar's space so text doesn't shift when it moves
const MARKER_WIDTH = 4;
const MARKER_GAP = 10;
const PROBE_TEXT = "0".repeat(50);

// Splits an over-long word (more characters than fit on a row) into
// row-sized chunks. Only the first chunk keeps the word's timing.
function splitLongWord(group, max) {
  const out = [];
  let cur = [];
  let len = 0;
  group.forEach((p) => {
    let text = p.text;
    let first = true;
    while (text.length) {
      if (len === max) {
        out.push(cur);
        cur = [];
        len = 0;
      }
      const take = text.slice(0, max - len);
      cur.push({ ...p, text: take, timed: p.timed && first });
      first = false;
      len += take.length;
      text = text.slice(take.length);
    }
  });
  if (cur.length) out.push(cur);
  return out;
}

// Wraps the script into display rows. The font is monospace, so wrapping is
// a character count; doing it here (rather than letting the browser wrap)
// means every row's word range is known exactly. Each
// row carries the word range [start, end) it covers. Bits are separated by
// a "gap" row, and blank lines become "blank" rows, so every row is the
// same height.
function layoutRows(linesByBit, charsPerRow) {
  const rows = [];
  let words = 0;
  const pushEmpty = (kind) => rows.push({ kind, groups: [], start: words, end: words });
  linesByBit.forEach((lines) => {
    if (!lines.length) return;
    if (rows.length) pushEmpty("gap");
    lines.forEach((pieces) => {
      if (pieces === null) {
        pushEmpty("blank");
        return;
      }
      // Adjacent non-space pieces (e.g. "word" + a punchline) wrap as one word
      const wordGroups = [];
      let cur = null;
      pieces.forEach((p) => {
        if (p.space) {
          cur = null;
          return;
        }
        if (!cur) {
          cur = [];
          wordGroups.push(cur);
        }
        cur.push(p);
      });

      let row = null;
      let len = 0;
      const place = (group, glen) => {
        if (!row || (row.groups.length && len + 1 + glen > charsPerRow)) {
          row = { kind: "text", groups: [], start: words, end: words };
          rows.push(row);
          len = 0;
        }
        len += (row.groups.length ? 1 : 0) + glen;
        row.groups.push(group);
        group.forEach((p) => {
          if (p.timed) row.end = ++words;
        });
      };
      wordGroups.forEach((group) => {
        const glen = group.reduce((n, p) => n + p.text.length, 0);
        if (glen > charsPerRow) {
          splitLongWord(group, charsPerRow).forEach((chunk) =>
            place(chunk, chunk.reduce((n, p) => n + p.text.length, 0))
          );
        } else {
          place(group, glen);
        }
      });
    });
  });
  return rows;
}

// One display row. Memoized so the clock ticking in the parent only
// re-renders the rows that gain or lose the current-row marker.
const Row = React.memo(function Row({ row, current, layout, onSeek }) {
  const style = { height: layout.rowHeight, lineHeight: `${layout.rowHeight}px`, fontSize: layout.fontSize };
  if (row.kind === "gap") {
    return (
      <div style={{ ...style, display: "flex", alignItems: "center" }}>
        <div style={{ flex: 1, borderTop: "1px dashed #cbd5e1" }} />
      </div>
    );
  }
  if (row.kind === "blank") return <div style={style} />;
  return (
    <div
      onClick={() => onSeek(row.start)}
      title="Jump here"
      className="cursor-pointer hover:bg-slate-100 rounded"
      style={{
        ...style,
        whiteSpace: "pre",
        borderLeft: `${MARKER_WIDTH}px solid ${current ? "#0b5d28" : "transparent"}`,
        paddingLeft: MARKER_GAP,
        transition: "border-color 200ms",
      }}
    >
      {row.groups.map((group, gIdx) => (
        <React.Fragment key={gIdx}>
          {gIdx > 0 && " "}
          {group.map((p, pIdx) => (
            <span
              key={pIdx}
              className={
                p.type === "punchline" ? "font-bold" : p.type === "stage" ? "text-gray-500 italic" : undefined
              }
            >
              {p.text}
            </span>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
});

// PracticeBody: a teleprompter. The whole script scrolls continuously; the
// current row always sits on the third visible line, marked with a bar at
// its left edge, with two rows of
// history above it and as much upcoming text as fits below. Each row's time
// comes from its word count, and when it's up the view scrolls one row.
// Clicking a row moves the playhead to the start of that row.
export default function PracticeBody({ bits = [], wordsPerMinute = DEFAULT_WORDS_PER_MINUTE }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);                // seconds of script time (already speed-scaled)
  const [playbackRate, setPlaybackRate] = useState(1.0);    // 1x by default
  const [layout, setLayout] = useState(null);               // { fontSize, rowHeight, charsPerRow }
  const elapsedRef = useRef(0);
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const probeRef = useRef(null);
  const prevRowRef = useRef(-1);
  const prevRowsRef = useRef(null);

  const wordsPerSecond = (wordsPerMinute || DEFAULT_WORDS_PER_MINUTE) / 60;

  const linesByBit = useMemo(
    () => (Array.isArray(bits) ? bits.map((bit) => buildLines(Array.isArray(bit?.content) ? bit.content : [])) : []),
    [bits]
  );

  // Word range [start, end) of each bit, for the bit name and prev/next.
  const bitRanges = useMemo(() => {
    let cursor = 0;
    return linesByBit.map((lines) => {
      const start = cursor;
      lines.forEach((pieces) => pieces && pieces.forEach((p) => p.timed && cursor++));
      return { start, end: cursor };
    });
  }, [linesByBit]);
  const totalWords = bitRanges.length ? bitRanges[bitRanges.length - 1].end : 0;
  const totalSeconds = totalWords / wordsPerSecond;

  const setClock = useCallback((seconds) => {
    elapsedRef.current = seconds;
    setElapsed(seconds);
  }, []);

  // Work out the font size and how many characters fit on a row, using the
  // bold character width so punchlines never overflow.
  const measure = useCallback(() => {
    const content = contentRef.current;
    const probe = probeRef.current;
    if (!content || !probe) return;
    const md = window.matchMedia?.("(min-width: 768px)").matches;
    const fontSize = md ? FONT_MD : FONT_SM;
    probe.style.fontSize = `${fontSize}px`;
    const charWidth = probe.getBoundingClientRect().width / PROBE_TEXT.length;
    const cs = window.getComputedStyle(content);
    const width =
      content.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - MARKER_WIDTH - MARKER_GAP;
    const charsPerRow = Math.max(8, Math.floor(width / charWidth));
    const rowHeight = Math.round(fontSize * 1.5);
    setLayout((prev) =>
      prev &&
      prev.fontSize === fontSize &&
      prev.charsPerRow === charsPerRow &&
      prev.rowHeight === rowHeight
        ? prev
        : { fontSize, rowHeight, charsPerRow }
    );
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    let lastWidth = content.clientWidth;
    const ro = new ResizeObserver(() => {
      if (content.clientWidth !== lastWidth) {
        lastWidth = content.clientWidth;
        measure();
      }
    });
    ro.observe(content);
    // The webfont can load after the first measure and change the character width
    let cancelled = false;
    document.fonts?.ready.then(() => !cancelled && measure());
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [measure]);

  const rows = useMemo(
    () => (layout ? layoutRows(linesByBit, layout.charsPerRow) : []),
    [linesByBit, layout]
  );

  // Keep the clock in range if the script gets shorter
  useEffect(() => {
    if (elapsedRef.current > totalSeconds) setClock(totalSeconds);
  }, [totalSeconds, setClock]);

  // Run the clock while playing
  useEffect(() => {
    if (!isPlaying) return;
    let last = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      const nextElapsed = Math.min(totalSeconds, elapsedRef.current + ((now - last) / 1000) * playbackRate);
      last = now;
      setClock(nextElapsed);
      if (nextElapsed >= totalSeconds) setIsPlaying(false);
    }, 100);
    return () => clearInterval(id);
  }, [isPlaying, playbackRate, totalSeconds, setClock]);

  const elapsedWords = elapsed * wordsPerSecond;

  // Current row: the first one whose time isn't up yet. Untimed rows (blank
  // lines, bit gaps, stage directions) take no time, so they pass straight
  // into history.
  let currentRow = rows.findIndex((r) => r.end > elapsedWords);
  if (currentRow === -1) currentRow = rows.length - 1;

  // Clicking a row jumps there, keeping the current play/pause state
  const seekToWord = useCallback((word) => setClock(word / wordsPerSecond), [wordsPerSecond, setClock]);

  // Every row is the same height and the content is padded by HISTORY_ROWS
  // rows, so scrolling by currentRow rows puts it on the third line.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !layout || currentRow < 0) return;
    const prev = prevRowRef.current;
    const relaid = prevRowsRef.current !== rows;
    if (!relaid && prev === currentRow) return;
    prevRowRef.current = currentRow;
    prevRowsRef.current = rows;
    const animate = !relaid && prev >= 0 && Math.abs(currentRow - prev) <= 3;
    scroller.scrollTo({ top: currentRow * layout.rowHeight, behavior: animate ? "smooth" : "auto" });
  }, [currentRow, rows, layout]);

  // Bit containing the current position (skipping bits with no spoken words)
  let bitIdx = bitRanges.findIndex((r) => r.end > elapsedWords && r.end > r.start);
  if (bitIdx === -1) bitIdx = Math.max(0, bitRanges.length - 1);

  const timedBits = bitRanges.map((r, i) => (r.end > r.start ? i : -1)).filter((i) => i >= 0);
  const prevBit = [...timedBits].reverse().find((i) => i < bitIdx);
  const nextBit = timedBits.find((i) => i > bitIdx);
  const canPrev = prevBit !== undefined;
  const canNext = nextBit !== undefined;

  // Jumping keeps the current play/pause state
  const goPrev = () => canPrev && seekToWord(bitRanges[prevBit].start);
  const goNext = () => canNext && seekToWord(bitRanges[nextBit].start);

  const togglePlay = () => {
    if (!isPlaying && elapsedRef.current >= totalSeconds) setClock(0); // replay from the top
    setIsPlaying((p) => !p);
  };

  const currentName =
    Array.isArray(bits) && bits[bitIdx]?.name
      ? bits[bitIdx].name
      : bitRanges.length
      ? `Bit ${bitIdx + 1} / ${bitRanges.length}`
      : "No bits";

  return (
    <div
      className="w-full flex-1 min-h-0 border rounded px-2 py-1 mt-1 bg-white flex flex-col"
      style={{
        fontFamily: "'Courier Prime', 'Courier New', monospace",
        minHeight: "120px",
      }}
    >
      {/* Top controls in one line: left (name), center (prev/play/next), right (spacer to keep it centered) */}
      <div
        className="w-full flex items-center justify-between"
        style={{ paddingTop: 4, paddingBottom: 4, flexShrink: 0 }}
      >
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <span className="text-base text-gray-700 font-medium truncate max-w-[40vw]">
            {currentName}
          </span>
        </div>

        {/* Center: Last, Play/Pause, Next */}
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            disabled={!canPrev}
            className="px-2 py-1 rounded text-lg"
            style={{ opacity: canPrev ? 1 : 0.5 }}
            title="Previous bit"
          >
            {"⬅️"}
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded text-lg"
            title={isPlaying ? "Pause" : "Play"}
            onClick={togglePlay}
          >
            {isPlaying ? "⏸" : "▶"}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={!canNext}
            className="px-2 py-1 rounded text-lg"
            style={{ opacity: canNext ? 1 : 0.5 }}
            title="Next bit"
          >
            {"➡️"}
          </button>
        </div>

        <div className="flex-1" />
      </div>

      {/* The whole script in one scroll view, filling the space between the controls */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0"
        style={{
          overflowY: "auto",
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          color: "#0f172a",
        }}
      >
        <div
          ref={contentRef}
          style={{
            position: "relative",
            // Blank rows above the first row, so it starts in the reading position
            padding: `${layout ? HISTORY_ROWS * layout.rowHeight : 0}px 16px 0`,
          }}
        >
          {/* Measures the character width; see measure() */}
          <span
            ref={probeRef}
            aria-hidden="true"
            className="font-bold"
            style={{ position: "absolute", visibility: "hidden", whiteSpace: "pre", top: 0, left: 0 }}
          >
            {PROBE_TEXT}
          </span>
          {layout &&
            rows.map((row, i) => (
              <Row
                key={i}
                row={row}
                current={i === currentRow}
                layout={layout}
                onSeek={seekToWord}
              />
            ))}
          {/* Room for the last row to scroll up into the reading position */}
          <div style={{ height: "100vh" }} />
        </div>
      </div>

      {/* Bottom-center speed controls */}
      <div
        className="w-full flex items-center justify-center gap-2"
        style={{
          paddingTop: 4,
          paddingBottom: 4,
          flexShrink: 0,
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <button
          type="button"
          className="px-2 py-1 rounded text-lg"
          title="Slower (-0.2x)"
          onClick={() => setPlaybackRate((r) => Math.max(0.4, parseFloat((r - 0.2).toFixed(2))))}
        >
          {"🐢"}
        </button>
        <span className="text-sm text-gray-700 font-medium">
          {playbackRate.toFixed(1)}x
        </span>
        <button
          type="button"
          className="px-2 py-1 rounded text-lg"
          title="Faster (+0.2x)"
          onClick={() => setPlaybackRate((r) => Math.min(3.0, parseFloat((r + 0.2).toFixed(2))))}
        >
          {"🐇"}
        </button>
      </div>
    </div>
  );
}
